import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetProposalAcceptanceQuery,
  useRecordProposalAcceptanceMutation,
} from '@/redux/internal-services/proposalsApi';
import { Button } from '@/components/UI/Button';

/** Client Accepted capture — records WHICH option the client agreed to before the engagement is
 *  allowed to claim acceptance.
 *
 *  Rendered by WorkflowCard's BUSINESS_CAPTURE_COMPONENTS registry against the
 *  `proposal_accepted_option` gate, so it takes that registry's fixed
 *  {engagementId, onScheduled, onCancel} shape (`onScheduled` = "the capture is done, fire the
 *  transition") rather than a prop set of its own.
 *
 *  The single-option case never reaches here: its gate is satisfied server-side (nothing to
 *  choose), so the transition just runs and its hook records the one option. This form therefore
 *  always has a real choice to present — but it still guards for a one-option payload rather than
 *  assuming, since the gate and this bundle can disagree across a deploy. */
const ProposalAcceptanceForm: React.FC<{
  engagementId: string;
  onScheduled?: () => void;
  onCancel?: () => void;
}> = ({ engagementId, onScheduled, onCancel }) => {
  const { t } = useTranslation();
  const { data: acceptance, isLoading } = useGetProposalAcceptanceQuery(engagementId);
  const [recordAcceptance, { isLoading: isSaving }] = useRecordProposalAcceptanceMutation();
  const [optionId, setOptionId] = useState('');
  const [error, setError] = useState('');

  const options = acceptance?.options ?? [];

  // Preselect what is ALREADY recorded (re-running the capture to correct a mis-click), else the
  // recommended option — which is options[0] by the proposal's own ordering, the one carrying the
  // headline price the client most likely responded to. Never a silent submit: the user still has
  // to confirm, because "probably this one" is exactly the assumption this step exists to stop.
  useEffect(() => {
    if (!acceptance || optionId) return;
    setOptionId(acceptance.accepted_option?.option_id || options[0]?.option_id || '');
  }, [acceptance, options, optionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!optionId) {
      setError(t('proposals.acceptancePickOption', 'Select which option the client accepted.'));
      return;
    }
    setError('');
    try {
      await recordAcceptance({ engagementId, option_id: optionId }).unwrap();
      onScheduled?.();
    } catch (err) {
      setError(
        (err as { data?: { message?: string } })?.data?.message
        || t('proposals.acceptanceFailed', 'Could not record the accepted option. Nothing has been changed.'),
      );
    }
  };

  if (isLoading) {
    return <p className="py-2 text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>;
  }

  if (!acceptance || options.length === 0) {
    return (
      <p className="py-2 text-xs text-danger-text">
        {t('proposals.acceptanceNoOptions', 'This proposal has no options to accept.')}
      </p>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="w-full space-y-3">
      {error && (
        <p className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger-text">{error}</p>
      )}

      <div>
        <label htmlFor="accepted-option" className="block text-sm font-medium text-ui-text mb-1">
          {t('proposals.acceptedOption', 'Accepted option')}
        </label>
        <select
          id="accepted-option"
          value={optionId}
          onChange={(e) => setOptionId(e.target.value)}
          disabled={isSaving}
          className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-ring disabled:opacity-60"
        >
          {options.map((o, i) => (
            <option key={o.option_id} value={o.option_id}>
              {/* display_label is snapshotted onto the proposal precisely so an option is
                  nameable here — falling back to the raw option_id would show
                  "EST-2026091415…", which no Sales Agent can match to what they sent. */}
              {o.display_label || o.option_id}
              {i === 0 ? ` — ${t('proposals.recommendedSuffix', 'recommended')}` : ''}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs leading-4 text-ui-text3">
          {t(
            'proposals.acceptanceOptionNote',
            'Only the options shown to the client on the sent proposal are listed.',
          )}
        </p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            disabled={isSaving}
            onClick={onCancel}
            className="h-6 min-h-0 w-auto px-4 py-1 text-sm"
          >
            {t('workflow.cancel', 'Cancel')}
          </Button>
        )}
        <Button type="submit" disabled={isSaving} className="h-6 min-h-0 w-auto px-4 py-1 text-sm">
          {isSaving
            ? t('proposals.acceptanceRecording', 'Recording…')
            : t('proposals.acceptanceConfirm', 'Confirm acceptance')}
        </Button>
      </div>

      <p className="pt-1 text-xs leading-4 text-ui-text3">
        {t(
          'proposals.acceptanceConfirmNote',
          'On confirm: records the accepted option on the proposal and moves the engagement to Proposal Accepted.',
        )}
      </p>
    </form>
  );
};

export default ProposalAcceptanceForm;
